import { useEffect } from 'react';
import { LAYERS as AXON_LAYERS } from '../Axon/useAxonGLNext';
import { LAYERS as EAGLE_EYE_LAYERS } from '../EagleEye/helpers';
import { default as IconLayer } from '../RSOSLayers/RSOSIconLayer';
import TrailingPathLayer from '../RSOSLayers/TrailingPathLayer';
import storeLayers from '../storeLayers';
import {
  getDynamicSpritesIcon,
  spritesImage,
  spritesMapping,
} from './getDynamicSpritesIcon';

const MAX_HISTORICAL_POSITIONS = 5;

const MARKER_SIZES = {
  sizeScale: 10, // this is the factor to scale the icon.
  sizeMinPixels: 30, // when zoomed out, the max width or height of the icon.
  sizeMaxPixels: 40, // when zoomed in, the max width or height of the icon.
};

const DynamicLayerGL = ({
  activeMarkerId,
  overlay,
  data: rawData,
  spatialLayerID = '',
  visible = true,
  onClick = () => {},
  onHover = () => {},
  isMapIconDisabled,
}) => {
  let data = rawData;

  // Axon is a special type of DSL...
  if (
    spatialLayerID === AXON_LAYERS.AXON_BODY_3 ||
    spatialLayerID === EAGLE_EYE_LAYERS.EAGLE_EYE
  ) {
    // If the data object has an exclude flag, definitely don't render it since
    // it's handled in AxonGLNext. Otherwise, continue and render it to be safe.
    if (Array.isArray(data)) {
      data = rawData.filter(d => !d.excludeFromDrawing);
      if (data.length === 0) {
        // if the exclude filter removed all the data, don't render anything.
        data = null;
      }
    }
  }

  useEffect(() => {
    if (data) {
      const filterOutPlaceholderData = data => {
        if (Array.isArray(data)) {
          return data.filter(d => d.external_id !== 'placeholder');
        }
        return data;
      };

      const trailingPathLayer = new TrailingPathLayer({
        id: `${spatialLayerID}-trailing-path-layer`,
        data: filterOutPlaceholderData(data),
        getPath: d => {
          const results = d.allCoordinates.map(coord => [coord.lng, coord.lat]);
          return results.slice(0, MAX_HISTORICAL_POSITIONS);
        },
        visible,
      });

      const iconLayer = new IconLayer({
        id: `${spatialLayerID}-icon-layer`,
        data,
        getPosition: d => {
          if (d.allCoordinates.length === 0) {
            return [0, 0];
          }

          const mostRecentCoordinate = d.allCoordinates[0];

          const coordinates = [
            mostRecentCoordinate.lng,
            mostRecentCoordinate.lat,
          ];

          return coordinates;
        },
        onClick: info => {
          if (info?.object?.layer?.key === 'avive_aed_placeholder') {
            // The placeholder is not a real spatial object, so there's no
            // need to open a MapCard for it. Ignore the click here.
            return;
          }
          onClick(info, spatialLayerID);
        },
        onHover: info => {
          if (info?.object?.layer?.key === 'avive_aed_placeholder') {
            // The placeholder is not a real spatial object, so there's no
            // need to open a tooltip for it. Ignore the hover here.
            return;
          }
          onHover(info, spatialLayerID);
        },
        pickable: !isMapIconDisabled,
        autoHighlight: true,
        highlightColor: [0, 0, 0, 15],
        iconAtlas: spritesImage,
        iconMapping: spritesMapping,
        sizeUnits: 'meters',
        ...MARKER_SIZES,
        getIcon: d => {
          let iconState =
            d.external_id === activeMarkerId ? 'active' : 'default';

          // This can come via WS or from the cache at page load.
          // At page load d?.layer?.key is missing, but d?.key is present and
          // set via actions/spatialLayers#fetchSpatialLayersSuccess.
          const key = d?.layer?.key || d?.key;

          if (key === 'avive_aed_request' && d?.attributes?.inactiveRequest) {
            iconState = 'inactive';
          }

          return getDynamicSpritesIcon(spatialLayerID, iconState, d);
        },
        visible,
        updateTriggers: {
          getIcon: [activeMarkerId],
        },
      });

      if (overlay) {
        storeLayers.save(trailingPathLayer);
        storeLayers.save(iconLayer);
        overlay.setLayers(storeLayers.getAll());
      }
    }
  }, [
    activeMarkerId,
    data,
    overlay,
    spatialLayerID,
    visible,
    onClick,
    onHover,
    isMapIconDisabled,
  ]);

  return null;
};

export default DynamicLayerGL;
